import React, { useContext, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import { DraftContext } from '../../../../../contexts/DraftContext'
import { useSidebarCollapse } from '../../../../../contexts/SidebarCollapseContext'
import { Gear, Eye, EyeSlash, FileBarGraph, Check2, Calendar, ChevronDown } from 'react-bootstrap-icons'
import { useParams } from 'react-router-dom'
import axiosService from '../../../../../utils/AxiosService'
import { toastSuccess, toastError } from '../../../../FormComponents/Toasts'
import Action from '../../../../../models/Action'
import ActionInsight from '../../../../../models/ActionInsight'
import DatePicker from '../../../../FormComponents/DatePicker'
import DraftStatusBadge from '../../../../Shared/DraftStatusBadge'
import { PROVIDER_ICONS, PROVIDER_COLORS } from '../../../../../utils/actionDisplay'
import { clientFeedbackSentOverride } from '../../../../../utils/draftStatus'
import InsightsSidebar from './ReviewWindowParts/InsightsSidebar'
import SetupActionModal from './ReviewWindowParts/SetupActionModal'
import OutsideClickClose from '../../../../OutsideClickClose'
import SubmissionForm from '../../../../Advertiser/DraftShare/SubmissionForm'
import SubmissionReview from './ReviewWindowParts/SubmissionReview'
import { useReadOnly } from '../../../../../contexts/ReadOnlyContext'

const EDITABLE_DRAFT_STATUSES = ['draft', 'requested', 'revision_requested']
import VersionHistorySection from './ReviewWindowParts/VersionHistorySection'

// Compact pill button standing in for the date input - matches the Figma
// reference's "due date" control (icon, formatted date, dropdown chevron)
// instead of the default full-width text input.
const DueDateInput = React.forwardRef(({ value, onClick }, ref) => (
  <button
    type="button"
    onClick={onClick}
    ref={ref}
    className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter dark:text-white whitespace-nowrap"
  >
    <Calendar /> {value || 'Select date'} <ChevronDown className="text-rb-mat-grey" />
  </button>
))

export default observer(function DraftReviewWindow() {
  const { id, action_id } = useParams()
  const { actions } = useContext(DraftContext)
  const { isCollapsed } = useSidebarCollapse()
  const readOnly = useReadOnly()
  const currentAction = useMemo(() => actions.find(action => +action.id === +action_id) || new Action(), [action_id, actions, actions.length])
  const actionDraft = currentAction.action_draft
  const isBeingReviewed = ['submitted', 'internally_approved', 'client_review'].includes(actionDraft?.draft_status)
  const statusOverride = clientFeedbackSentOverride('pm', actionDraft?.draft_status, actionDraft?.latestVersion?.client_feedback_submitted)

  const [showSetupModal, setShowSetupModal] = useState(false)
  const [showInsightsSidebar, setShowInsightsSidebar] = useState(false)

  const Icon = PROVIDER_ICONS[currentAction.provider]
  const pmApiBase = `/api/project_manager/campaigns/${id}/actions/${action_id}`

  // Legacy visibility toggle, still read by the not-yet-rebuilt Advertiser
  // client-review page.
  const toggleLegacyClientVisibility = () => {
    axiosService.patch(`/api/project_manager/campaigns/${id}/actions/${action_id}`, { action_data: { draft_is_visible: !currentAction.draft_is_visible } })
      .then(updated => {
        currentAction.draft_is_visible = updated.draft_is_visible
        toastSuccess(updated.draft_is_visible ? 'Visible to client' : 'Hidden from client')
      })
      .catch(({ data: { error } }) => toastError(error))
  }

  // Legacy: draft_accepted is what creates the ActionInsight record
  // server-side (Action#create_action_insight). Kept for the not-yet-cut-over
  // Advertiser client-review page, which still reads this flag.
  const toggleLegacyAccepted = () => {
    axiosService.patch(`/api/project_manager/campaigns/${id}/actions/${action_id}`, { action_data: { draft_accepted: !currentAction.draftAccepted } })
      .then(updated => {
        currentAction.draftAccepted = updated.draft_accepted
        currentAction.action_insight = new ActionInsight(updated.action_insight)
        toastSuccess(updated.draft_accepted ? 'Accepted' : 'No longer accepted')
      })
      .catch(({ data: { error } }) => toastError(error))
  }

  // Every other field on this screen (caption, notes, co-author, checklist)
  // autosaves itself on change/blur - due date used to be the one exception,
  // requiring a separate "Save Draft" click that saved nothing else and was
  // easy to mistake for "save the whole draft". Autosaving here too removes
  // that button and the confusion along with it.
  const saveDueDate = (date) => {
    axiosService.patch(`/api/project_manager/campaigns/${id}/actions/${action_id}/action_draft`, { action_draft: { due_date: date } })
      .then(updated => {
        actionDraft.due_date = updated.due_date
        toastSuccess('Due date saved')
      })
      .catch(({ data: { error } }) => toastError(error))
  }

  if (!actionDraft) return null

  const leftPosition = isCollapsed ? 479 : 575
  const width = isCollapsed ? 'calc(100vw - 479px)' : 'calc(100vw - 575px)'

  return (
    <div
      className="fixed top-[52px] flex flex-col items-center overflow-y-auto transition-all duration-300"
      style={{ left: `${leftPosition}px`, height: 'calc(100vh - 52px)', width }}
    >
      <div className="my-5 p-3 rounded-lg bg-white dark:bg-rb-dark-mode flex justify-between items-center w-[650px] border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter">
        <div className="flex items-center gap-2">
          {Icon && <Icon className={PROVIDER_COLORS[currentAction.provider]} />}
          <div>
            <h2 className="font-bold text-lg dark:text-white">{currentAction.name}</h2>
            <span className="text-sm text-rb-mat-grey dark:text-rb-dark-mode-text-grey">{currentAction.influencer_name} @{currentAction.auth_username}</span>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <DraftStatusBadge draftStatus={actionDraft.draft_status} {...statusOverride} />

          <button onClick={toggleLegacyClientVisibility} disabled={readOnly} className={readOnly ? 'cursor-default' : ''} title={currentAction.draft_is_visible ? 'Visible to client' : 'Hidden from client'}>
            {currentAction.draft_is_visible ? <Eye className="text-blue-500" /> : <EyeSlash className="text-rb-mat-grey" />}
          </button>

          <button
            onClick={toggleLegacyAccepted}
            disabled={readOnly}
            title={currentAction.draftAccepted ? 'Accepted' : 'Accept'}
            className={`${currentAction.draftAccepted ? 'text-rb-green' : 'text-rb-mat-grey'} ${readOnly ? 'cursor-default' : ''}`}
          >
            <Check2 />
          </button>

          {currentAction.action_insight && currentAction.draftAccepted && (
            <button onClick={() => setShowInsightsSidebar(true)} title="Insights">
              <FileBarGraph className="dark:text-white" />
            </button>
          )}

          {!readOnly && (
            <button
              onClick={() => setShowSetupModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium rounded-lg border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter dark:text-white whitespace-nowrap"
            >
              <Gear /> Action Settings
            </button>
          )}
        </div>
      </div>

      <div className="border-1 border-rb-widget-border dark:border-rb-dark-mode-lighter rounded-lg p-4 w-[650px] mb-4 bg-white dark:bg-rb-dark-mode flex items-center justify-between">
        <p className="font-bold dark:text-white whitespace-nowrap">1. Due Date</p>
        {readOnly ? (
          <span className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium dark:text-white whitespace-nowrap">
            <Calendar /> {actionDraft.due_date ? new Date(actionDraft.due_date).toLocaleDateString('de-DE') : 'No due date'}
          </span>
        ) : (
          <DatePicker
            selected={actionDraft.due_date ? new Date(actionDraft.due_date) : null}
            onChange={saveDueDate}
            customInput={<DueDateInput />}
            dateFormat="dd.MM.yyyy"
            wrapperClassName="w-auto"
            containerClassName="w-auto"
            topMargin=""
            minDate={new Date()}
          />
        )}
      </div>

      <VersionHistorySection actionDraft={actionDraft} />

      {isBeingReviewed ? (
        <SubmissionReview action={currentAction} />
      ) : readOnly && EDITABLE_DRAFT_STATUSES.includes(actionDraft.draft_status) ? null : (
        // PM filling this in directly - e.g. content the influencer sent
        // over email/WhatsApp instead of using their link. Same
        // form/endpoints as the influencer's own portal, just
        // PM-authenticated.
        <SubmissionForm action={currentAction} apiBase={pmApiBase} role="pm" />
      )}

      <SetupActionModal actionDraft={actionDraft} isOpen={showSetupModal} onClose={() => setShowSetupModal(false)} />

      {showInsightsSidebar && (
        <OutsideClickClose isOpen={showInsightsSidebar} onClose={() => setShowInsightsSidebar(false)}>
          <InsightsSidebar onClose={() => setShowInsightsSidebar(false)} insight={currentAction.action_insight} />
        </OutsideClickClose>
      )}
    </div>
  )
})
